import React from "react";
import "./CampusPage.css";

import janaschool from "./ourcampusignite.png";
import schoolHistoryImg from "./tiedye.jpg";

import Footer from "./Footer";

import {
    FaBuilding,
    FaSchool,
    FaArrowRight,
    FaTools,
    FaGraduationCap,
    FaLaptop,
    FaFlask,
    FaUsers,
    FaBookOpen,
    FaCheckCircle,
    FaHardHat,
    FaLightbulb,
    FaCogs,
    FaChalkboardTeacher,
    FaWrench,
    FaIndustry,
    FaShieldAlt,
} from "react-icons/fa";

import { motion } from "framer-motion";

const CampusPage = () => {
    const campusFeatures = [
        {
            icon: <FaTools />,
            title: "Practical Workshops",
            text: "Purpose-built spaces where students develop practical skills through hands-on vocational training.",
        },
        {
            icon: <FaLaptop />,
            title: "Technology Learning",
            text: "Modern technology and digital learning spaces that prepare students for today's changing workplace.",
        },
        {
            icon: <FaChalkboardTeacher />,
            title: "Training Classrooms",
            text: "Comfortable and focused learning environments designed for both theory and practical instruction.",
        },
        {
            icon: <FaWrench />,
            title: "Skills Development",
            text: "Students learn by doing, developing technical abilities, confidence, creativity, and problem-solving skills.",
        },
        {
            icon: <FaUsers />,
            title: "Collaborative Spaces",
            text: "Open learning environments that encourage teamwork, communication, leadership, and peer learning.",
        },
        {
            icon: <FaShieldAlt />,
            title: "Safe Environment",
            text: "A structured campus environment where safety, responsibility, discipline, and professionalism are emphasized.",
        },
    ];

    const trainingAreas = [
        {
            icon: <FaCogs />,
            title: "Technical Training",
            text: "Practical training designed to build real-world technical and occupational skills.",
        },
        {
            icon: <FaIndustry />,
            title: "Industry Preparation",
            text: "Students are introduced to professional standards, workplace practices, and career readiness.",
        },
        {
            icon: <FaLightbulb />,
            title: "Innovation & Creativity",
            text: "Students are encouraged to think creatively, solve problems, and develop useful ideas.",
        },
        {
            icon: <FaGraduationCap />,
            title: "Career Development",
            text: "Learning experiences help students connect education with future employment and entrepreneurship.",
        },
    ];

    return (
        <div className="campus-page">

            {/* HERO */}
            <section
                className="campus-hero"
                style={{
                    backgroundImage: `url(${janaschool})`,
                }}
            >
                <div className="campus-overlay"></div>

                <div className="campus-hero-shape campus-shape-one"></div>
                <div className="campus-hero-shape campus-shape-two"></div>

                <motion.div
                    className="campus-title"
                    initial={{ opacity: 0, y: 60 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1 }}
                >
                    <div className="campus-eyebrow">
                        <span></span>
                        IGNITE S VOCATIONAL SCHOOL
                        <span></span>
                    </div>

                    <h1>
                        Our <strong>Campus</strong>
                    </h1>

                    <p>
                        A dynamic environment where education,
                        practical training, innovation, and career
                        development come together.
                    </p>

                    <div className="campus-hero-buttons">
                        <a href="#campus-experience" className="campus-primary-btn">
                            Explore Our Campus
                            <FaArrowRight />
                        </a>

                        <a href="#training-environment" className="campus-outline-btn">
                            Discover Our Facilities
                        </a>
                    </div>
                </motion.div>

                <div className="campus-scroll-indicator">
                    <span>Scroll to explore</span>
                    <div></div>
                </div>
            </section>


            {/* INTRODUCTION */}
            <section
                className="campus-introduction"
                id="campus-experience"
            >
                <div className="campus-intro-container">

                    <motion.div
                        className="campus-intro-content"
                        initial={{ opacity: 0, x: -70 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <div className="campus-section-label">
                            <span></span>
                            THE IGNITE EXPERIENCE
                        </div>

                        <h2>
                            More Than a School.
                            <br />
                            <span>A Place to Build Skills.</span>
                        </h2>

                        <p>
                            At Ignite S Vocational School, the campus is
                            designed to support a learning experience that
                            connects classroom knowledge with practical
                            skills.
                        </p>

                        <p>
                            Our environment encourages students to learn,
                            practice, collaborate, create, and prepare for
                            meaningful careers. Every learning space
                            contributes to developing capable, confident,
                            and responsible young professionals.
                        </p>

                        <div className="campus-check-list">

                            <div>
                                <FaCheckCircle />
                                <span>Hands-on practical learning</span>
                            </div>

                            <div>
                                <FaCheckCircle />
                                <span>Professional learning environment</span>
                            </div>

                            <div>
                                <FaCheckCircle />
                                <span>Technology-supported education</span>
                            </div>

                            <div>
                                <FaCheckCircle />
                                <span>Career and entrepreneurship preparation</span>
                            </div>

                        </div>

                    </motion.div>


                    <motion.div
                        className="campus-intro-image"
                        initial={{ opacity: 0, x: 70 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >

                        <div className="campus-image-main">
                            <img
                                src={schoolHistoryImg}
                                alt="Ignite S Vocational School students"
                            />
                        </div>

                        <div className="campus-image-card">
                            <FaSchool />

                            <div>
                                <strong>Learning</strong>
                                <span>Through Experience</span>
                            </div>
                        </div>

                        <div className="campus-image-decoration"></div>

                    </motion.div>

                </div>
            </section>


            {/* CAMPUS FEATURES */}
            <section
                className="campus-features"
                id="training-environment"
            >

                <div className="campus-feature-background"></div>

                <div className="campus-section-heading">

                    <motion.div
                        className="campus-section-label center"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <span></span>
                        OUR LEARNING ENVIRONMENT
                        <span></span>
                    </motion.div>

                    <motion.h2
                        initial={{ opacity: 0, y: 25 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        viewport={{ once: true }}
                    >
                        A Campus Built for
                        <span> Practical Learning</span>
                    </motion.h2>

                    <p>
                        From classrooms to practical training spaces,
                        our campus supports the complete vocational
                        learning journey.
                    </p>

                </div>


                <div className="campus-feature-grid">

                    {campusFeatures.map((feature, index) => (
                        <motion.div
                            className="campus-feature-card"
                            key={feature.title}
                            initial={{
                                opacity: 0,
                                y: 40,
                            }}
                            whileInView={{
                                opacity: 1,
                                y: 0,
                            }}
                            transition={{
                                duration: 0.6,
                                delay: index * 0.08,
                            }}
                            viewport={{ once: true }}
                        >

                            <div className="campus-feature-number">
                                0{index + 1}
                            </div>

                            <div className="campus-feature-icon">
                                {feature.icon}
                            </div>

                            <h3>{feature.title}</h3>

                            <p>{feature.text}</p>

                            <div className="campus-feature-line"></div>

                        </motion.div>
                    ))}

                </div>

            </section>


            {/* VOCATIONAL TRAINING SECTION */}
            <section className="campus-training">

                <div className="campus-training-container">

                    <motion.div
                        className="campus-training-image"
                        initial={{ opacity: 0, scale: 0.94 }}
                        whileInView={{
                            opacity: 1,
                            scale: 1,
                        }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >

                        <img
                            src={janaschool}
                            alt="Students learning at Ignite S Vocational School"
                        />

                        <div className="training-image-overlay"></div>

                        <div className="training-floating-card">

                            <FaHardHat />

                            <div>
                                <strong>Learn</strong>
                                <span>Practice. Create. Excel.</span>
                            </div>

                        </div>

                    </motion.div>


                    <motion.div
                        className="campus-training-content"
                        initial={{ opacity: 0, x: 70 }}
                        whileInView={{
                            opacity: 1,
                            x: 0,
                        }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >

                        <div className="campus-section-label">
                            <span></span>
                            VOCATIONAL EDUCATION
                        </div>

                        <h2>
                            Where Knowledge
                            <br />
                            Becomes <span>Skill</span>
                        </h2>

                        <p>
                            Vocational education is most effective when
                            students have opportunities to connect what
                            they learn in class with practical experience.
                        </p>

                        <p>
                            Ignite S Vocational School provides an
                            environment where students can develop
                            technical knowledge, practical competence,
                            teamwork, creativity, and professional
                            responsibility.
                        </p>


                        <div className="training-area-grid">

                            {trainingAreas.map((area) => (
                                <div
                                    className="training-area"
                                    key={area.title}
                                >
                                    <div className="training-area-icon">
                                        {area.icon}
                                    </div>

                                    <div>
                                        <h4>{area.title}</h4>
                                        <p>{area.text}</p>
                                    </div>
                                </div>
                            ))}

                        </div>

                    </motion.div>

                </div>

            </section>


            {/* CAMPUS STATS */}
            <section className="campus-stats">

                <div className="campus-stats-overlay"></div>

                <div className="campus-stats-container">

                    <motion.div
                        className="campus-stat"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <FaSchool />
                        <strong>01</strong>
                        <span>Learning Environment</span>
                    </motion.div>

                    <motion.div
                        className="campus-stat"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        viewport={{ once: true }}
                    >
                        <FaTools />
                        <strong>06</strong>
                        <span>Practical Training</span>
                    </motion.div>

                    <motion.div
                        className="campus-stat"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        viewport={{ once: true }}
                    >
                        <FaUsers />
                        <strong>Pending</strong>
                        <span>Student Collaboration</span>
                    </motion.div>

                    <motion.div
                        className="campus-stat"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        viewport={{ once: true }}
                    >
                        <FaGraduationCap />
                        <strong>Pending</strong>
                        <span>Career Preparation</span>
                    </motion.div>

                </div>

            </section>


            {/* CTA */}
            <section className="campus-cta">

                <motion.div
                    initial={{ opacity: 0, y: 35 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                    viewport={{ once: true }}
                >

                    <FaGraduationCap className="campus-cta-icon" />

                    <h2>
                        Step Into Your Future
                    </h2>

                    <p>
                        Discover a learning environment designed to
                        help students develop practical skills,
                        confidence, creativity, and career readiness.
                    </p>

                    <a
                        href="/contact"
                        className="campus-cta-button"
                    >
                        Connect With Us
                        <FaArrowRight />
                    </a>

                </motion.div>

            </section>


            <Footer />

        </div>
    );
};

export default CampusPage;